import { getTreeEntities, getTreeRelations } from "api/entities";
import { getTreeById } from "api/trees";
import type { EntityDto, TreeDto, TreeRelationDto } from "api/types";
import { PageLoader, TreeAccessNotice, type TreeAccessNoticeProps } from "components";
import { useTreeSeo } from "hooks/useTreeSeo";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Paths, { getTreeEditPath, getTreeFullViewPath, getTreeViewPath } from "routes/Paths";
import { useSession } from "Session";
import { startRequest } from "utilities";

import TreeEditor from "../HomePage/components/TreeEditor";

type ApiError = {
	status?: number;
};

/**
 * Full-page, read-only version of the tree editor layout. Reached from the tree view page's
 * embedded canvas; an optional `entity` query parameter pre-selects a node.
 */
const TreeFullViewPage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { getAccessToken, hasSession, signOut } = useSession();
	const [tree, setTree] = useState<TreeDto | null>(null);
	const [accessError, setAccessError] = useState<TreeAccessNoticeProps["reason"] | null>(null);
	useTreeSeo(tree, id);
	const [entities, setEntities] = useState<EntityDto[]>([]);
	const [relations, setRelations] = useState<TreeRelationDto[]>([]);
	// Entities arrive a round trip after the tree, so the canvas keeps loading after the shell renders.
	const [isCanvasLoading, setIsCanvasLoading] = useState(true);
	const initialSelectedEntityId = searchParams.get("entity");
	const onBack = useCallback((): void => navigate(Paths.root), [navigate]);
	const onOpenTreeView = useCallback((): void => {
		if (id) {
			navigate(getTreeViewPath(id));
		}
	}, [id, navigate]);
	// The editor opens with nothing selected: switching modes closes the selected node's ring rather
	// than reopening it on the other side.
	const onEditTree = useCallback((): void => {
		if (id) {
			navigate(getTreeEditPath(id));
		}
	}, [id, navigate]);
	// Keep the selected entity in the URL so refresh and sharing preserve the selection.
	const onSelectedEntityChange = useCallback(
		(entityId: string | null): void => {
			setSearchParams(entityId ? { entity: entityId } : {}, { replace: true });
		},
		[setSearchParams]
	);

	const loadTree = useCallback(async (): Promise<void> => {
		if (!id) {
			return;
		}

		try {
			// Anonymous viewers have no token; public trees are readable without one.
			const token = await getAccessToken();

			// All three requests go out together: nothing here depends on the tree row, so awaiting it
			// first only added a round trip in front of the entity payload. See TreeViewPage for why the
			// old `entityCount > 0` shortcut was not worth that cost.
			const treeRequest = startRequest(getTreeById(id, token));
			const entitiesRequest = startRequest(getTreeEntities(id, token));
			const relationsRequest = startRequest(getTreeRelations(id, token));

			// Setting the tree first swaps the full-page loader for the editor shell — title, sidebar and
			// a canvas spinner — instead of holding a blank screen until the entities land.
			const loaded = await treeRequest;
			setTree(loaded);

			const [treeEntities, treeRelations] = await Promise.all([entitiesRequest, relationsRequest]);

			setEntities(treeEntities);
			setRelations(treeRelations);
			setIsCanvasLoading(false);
		} catch (error: unknown) {
			const apiError = error as ApiError;

			if (hasSession && apiError.status === 401) {
				// Stale session: dropping it remounts the page anonymously, which retries the load.
				signOut();

				return;
			}

			// The notice below replaces the canvas, but stop the spinner regardless so nothing is left
			// claiming to be busy.
			setIsCanvasLoading(false);

			// Same terminal state as the tree view page: explain it here rather than bouncing to sign-in,
			// so the URL stays shareable and a signed-out member can sign back in from this page.
			setAccessError(apiError.status === 403 || apiError.status === 401 ? "noAccess" : "notFound");
		}
	}, [getAccessToken, hasSession, id, signOut]);

	const onEntityCreated = useCallback((): void => {
		loadTree().catch(() => undefined);
	}, [loadTree]);

	useEffect(() => {
		loadTree().catch(() => undefined);
	}, [loadTree]);

	if (accessError) {
		return (
			<TreeAccessNotice
				hasSession={hasSession}
				reason={accessError}
				returnTo={id ? getTreeFullViewPath(id) : Paths.root}
			/>
		);
	}

	// Nothing of this page exists before the tree does, so the whole screen is the loading state.
	if (!tree) {
		return <PageLoader label={t("view.loadingTree")} />;
	}

	const canEdit = tree.myRole === "Editor" || tree.myRole === "Admin";

	return (
		<TreeEditor
			collaborators={tree.collaborators}
			entities={entities}
			initialSelectedEntityId={initialSelectedEntityId}
			isLoading={isCanvasLoading && tree.entityCount > 0}
			onBack={onBack}
			onEditTree={canEdit ? onEditTree : undefined}
			onEntityCreated={onEntityCreated}
			onOpenTreeView={onOpenTreeView}
			onSelectedEntityChange={onSelectedEntityChange}
			relations={relations}
			treeId={tree.id}
			treeTitle={tree.title}
			treeVisibility={tree.visibility}
			readOnly
		/>
	);
};

export default TreeFullViewPage;
